// src/pages/finance/FinanceDashboardPage.tsx
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  LockKeyhole,
  PieChart as PieChartIcon,
  Receipt,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart as RePieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_CONFIG, fetchApp } from "../../services/apiService";

type PaymentStatus = "completed" | "pending" | "failed" | string;
type InvoiceStatus = "PENDING" | "PAID" | "EXPIRED" | "CANCELLED" | string;

interface Payment {
  id: string;
  invoice_number?: string | null;
  billing_account_id?: string | null;
  secret_id?: string | null;
  secret_username?: string | null;
  plan_name?: string | null;
  amount_cents: number;
  currency?: string;
  status: PaymentStatus;
  external_reference?: string | null;
  idempotency_key?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  payment_metadata?: {
    msisdn?: string;
    bill_ref_number?: string;
    business_short_code?: string;
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    client_id?: string;
  } | null;
}

interface Invoice {
  id: string;
  invoice_number: string;
  billing_account_id?: string | null;
  secret_id?: string | null;
  secret_username?: string | null;
  plan_name?: string | null;
  status: InvoiceStatus;
  currency?: string;
  total_amount_cents: number;
  paid_amount_cents: number;
  period_start?: string | null;
  period_end?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface MpesaTx {
  id: string;
  trans_amount?: number | string | null;
  amount?: number | string | null;
  raw_request?: {
    TransAmount?: string | number;
    MSISDN?: string;
    TransID?: string;
    BillRefNumber?: string;
    BusinessShortCode?: string;
    FirstName?: string;
    MiddleName?: string;
    LastName?: string;
  } | null;
  business_short_code?: string | null;
  processed?: boolean;
  bill_ref_number?: string | null;
  process_error?: string | null;
  payment_id?: string | null;
  msisdn?: string | null;
  trans_id?: string | null;
  received_at?: string | null;
  processed_at?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
}

interface Expense {
  id: string;
  client_id?: string;
  amount: string | number;
  currency?: string;
  category?: string | null;
  description?: string | null;
  expense_date?: string | null;
  created_at?: string | null;
  created_by_id?: string | null;
  created_by_name?: string | null;
}

interface CustomerFinanceStats {
  ok: boolean;
  total_customers: number;
  active_customers: number;
  inactive_customers: number;
  new_this_month: number;
  expected_monthly_amount: number;
  expected_monthly_cents: number;
  all_customers_expected_amount: number;
  all_customers_expected_cents: number;
}

interface TopCustomerRow {
  key: string;
  username: string;
  plan_name?: string | null;
  totalReceivedCents: number;
  paymentsCount: number;
  lastPaidAt?: string | null;
}

interface TalkSasaBalance {
  ok: boolean;
  balance: number | null;
  currency: string | null;
}

const PALETTE = [
  "#10b981",
  "#06b6d4",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#2563eb",
  "#6366f1",
];

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "#10b981",
  PAID: "#10b981",
  PENDING: "#f59e0b",
  FAILED: "#ef4444",
  CANCELLED: "#64748b",
  EXPIRED: "#ef4444",
  UNKNOWN: "#94a3b8",
};

const CHART_GRID = { stroke: "#cbd5e1", strokeDasharray: "3 3" };
const AXIS_STYLE = { fontSize: 10, fill: "#64748b", fontFamily: "inherit" };

const normalizeList = <T,>(res: any): T[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const cleanStatus = (value?: string | null) =>
  String(value || "UNKNOWN")
    .trim()
    .toUpperCase();

const isCompletedPayment = (status?: string | null) =>
  cleanStatus(status) === "COMPLETED";

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getPaymentDate = (p: Payment) =>
  toDate(p.paid_at || p.created_at || null);

const getExpenseDate = (e: Expense) =>
  toDate(e.expense_date || e.created_at || null);

const getMpesaDate = (tx: MpesaTx) =>
  toDate(tx.received_at || tx.processed_at || null);

const expenseToCents = (e: Expense): number => {
  const raw =
    typeof e.amount === "string" ? parseFloat(e.amount) : Number(e.amount);

  return Number.isFinite(raw) ? Math.round(raw * 100) : 0;
};

const mpesaToCents = (tx: MpesaTx): number => {
  if (tx.trans_amount != null) {
    const value = Number(tx.trans_amount);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  if (tx.amount != null) {
    const value = Number(tx.amount);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  if (tx.raw_request?.TransAmount != null) {
    const value = Number(tx.raw_request.TransAmount);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  return 0;
};

const monthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const dayKey = (date: Date) => date.toISOString().slice(0, 10);

const formatKES = (cents: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(
    Math.round((cents || 0) / 100),
  )}`;

const formatKESValue = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(Math.round(value || 0))}`;

const formatShortDate = (value?: string | null) => {
  const date = toDate(value);
  if (!date) return "—";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const formatDate = (value?: string | null) => {
  const date = toDate(value);
  if (!date) return "—";

  return date.toLocaleDateString("en-KE", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const percent = (part: number, total: number) => {
  if (!total) return "0.0";
  return ((part / total) * 100).toFixed(1);
};

const normalizeForSearch = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getCustomerFinanceStats = (value: any): CustomerFinanceStats => {
  return {
    ok: Boolean(value?.ok ?? true),
    total_customers: Number(value?.total_customers ?? 0),
    active_customers: Number(value?.active_customers ?? 0),
    inactive_customers: Number(value?.inactive_customers ?? 0),
    new_this_month: Number(value?.new_this_month ?? 0),
    expected_monthly_amount: Number(value?.expected_monthly_amount ?? 0),
    expected_monthly_cents: Number(value?.expected_monthly_cents ?? 0),
    all_customers_expected_amount: Number(
      value?.all_customers_expected_amount ?? 0,
    ),
    all_customers_expected_cents: Number(
      value?.all_customers_expected_cents ?? 0,
    ),
  };
};

type Tone = "emerald" | "sky" | "amber" | "rose" | "violet" | "slate" | "cyan";

const toneClasses: Record<
  Tone,
  {
    card: string;
    icon: string;
    text: string;
    ring: string;
  }
> = {
  emerald: {
    card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
    icon: "bg-emerald-100 text-emerald-700",
    text: "text-emerald-700",
    ring: "bg-emerald-200/50",
  },
  sky: {
    card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
    icon: "bg-sky-100 text-sky-700",
    text: "text-sky-700",
    ring: "bg-sky-200/50",
  },
  cyan: {
    card: "border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50 shadow-cyan-200/50",
    icon: "bg-cyan-100 text-cyan-700",
    text: "text-cyan-700",
    ring: "bg-cyan-200/50",
  },
  amber: {
    card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
    icon: "bg-amber-100 text-amber-700",
    text: "text-amber-700",
    ring: "bg-amber-200/50",
  },
  rose: {
    card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
    icon: "bg-rose-100 text-rose-700",
    text: "text-rose-700",
    ring: "bg-rose-200/50",
  },
  violet: {
    card: "border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 shadow-violet-200/50",
    icon: "bg-violet-100 text-violet-700",
    text: "text-violet-700",
    ring: "bg-violet-200/50",
  },
  slate: {
    card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
    icon: "bg-slate-200 text-slate-700",
    text: "text-slate-600",
    ring: "bg-slate-200/60",
  },
};

interface KpiCardProps {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  tone: Tone;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub, icon, tone }) => {
  const toneStyle = toneClasses[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${toneStyle.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${toneStyle.ring}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${toneStyle.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${toneStyle.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${toneStyle.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

interface MovementCardProps {
  label: string;
  value: string;
  last: string;
  diffCents?: number;
  diffPercent?: string;
  tone?: "good" | "bad" | "neutral";
}

const MovementCard: React.FC<MovementCardProps> = ({
  label,
  value,
  last,
  diffCents = 0,
  diffPercent = "0.0",
  tone,
}) => {
  const up = diffCents >= 0;
  const resolvedTone = tone || (up ? "good" : "bad");

  const badgeClass =
    resolvedTone === "neutral"
      ? "border-slate-300 bg-slate-200 text-slate-600"
      : resolvedTone === "good"
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-rose-200 bg-rose-100 text-rose-700";

  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-5 shadow-lg shadow-slate-300/25">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
            {label}
          </p>

          <p className="mt-1 text-2xl font-black tracking-tight text-slate-900">
            {value}
          </p>

          <p className="mt-1 text-[11.5px] font-medium text-slate-500">
            {last}
          </p>
        </div>

        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-black ${badgeClass}`}
        >
          {up ? (
            <ArrowUpRight className="h-3.5 w-3.5" />
          ) : (
            <ArrowDownRight className="h-3.5 w-3.5" />
          )}
          {up ? "+" : ""}
          {diffPercent}%
        </span>
      </div>
    </div>
  );
};

const PanelWrap: React.FC<{
  title: string;
  hint?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, hint, right, children, className = "" }) => (
  <div
    className={`overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25 ${className}`}
  >
    <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-100/80 px-5 py-4">
      <div>
        <p className="text-[14px] font-black text-slate-900">{title}</p>
        {hint && (
          <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
            {hint}
          </p>
        )}
      </div>

      {right && <div className="shrink-0">{right}</div>}
    </div>

    <div className="p-5">{children}</div>
  </div>
);

const EmptyState: React.FC<{ message: string; detail: string }> = ({
  message,
  detail,
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-3xl bg-slate-200 text-slate-500">
      <FileText className="h-6 w-6" />
    </div>

    <p className="text-[13px] font-black text-slate-800">{message}</p>

    <p className="mt-1 max-w-xs text-[12px] font-medium text-slate-500">
      {detail}
    </p>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const clean = cleanStatus(status);

  const cls =
    clean === "COMPLETED" || clean === "PAID"
      ? "border-emerald-200 bg-emerald-100 text-emerald-800"
      : clean === "PENDING"
        ? "border-amber-200 bg-amber-100 text-amber-800"
        : clean === "FAILED" || clean === "EXPIRED"
          ? "border-rose-200 bg-rose-100 text-rose-700"
          : "border-slate-300 bg-slate-200 text-slate-600";

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-[10.5px] font-black ${cls}`}
    >
      {clean.toLowerCase()}
    </span>
  );
};

const KesTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-2xl bg-slate-900 px-3 py-2 text-[11px] text-white shadow-xl">
      {label && <p className="mb-1 text-slate-300">{label}</p>}

      {payload.map((p: any) => (
        <p key={p.dataKey || p.name} className="font-bold">
          {p.name}: {formatKESValue(Number(p.value || 0))}
        </p>
      ))}
    </div>
  );
};

const CountTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-2xl bg-slate-900 px-3 py-2 text-[11px] text-white shadow-xl">
      {label && <p className="mb-1 text-slate-300">{label}</p>}

      {payload.map((p: any) => (
        <p key={p.dataKey || p.name} className="font-bold">
          {p.name || "Count"}: {p.value}
        </p>
      ))}
    </div>
  );
};

const PaymentStatusTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;

  if (!item) return null;

  return (
    <div className="rounded-2xl bg-slate-900 px-3 py-2 text-[11px] text-white shadow-xl">
      <p className="font-bold">{item.name}</p>
      <p className="text-slate-200">Payments: {item.value}</p>
      <p className="text-slate-200">Amount: {formatKES(item.amountCents)}</p>
      <p className="text-slate-300">{item.percent}% of payments</p>
    </div>
  );
};

const FinanceDashboardPage: React.FC = () => {
  const [statsUnlocked, setStatsUnlocked] = useState(false);
  const [showUnlockPage, setShowUnlockPage] = useState(false);
  const [dashboardPassword, setDashboardPassword] = useState("");
  const [showDashboardPassword, setShowDashboardPassword] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [mpesa, setMpesa] = useState<MpesaTx[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [customerStats, setCustomerStats] =
    useState<CustomerFinanceStats | null>(null);
  const [talkSasaBalance, setTalkSasaBalance] =
    useState<TalkSasaBalance | null>(null);

  const [packageSearch, setPackageSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = async () => {
    setLoading((prev) => prev || (!payments.length && !invoices.length));
    setRefreshing(true);
    setError(null);

    try {
      const [
        paymentsRes,
        invoicesRes,
        mpesaRes,
        customerStatsRes,
        expensesRes,
        talkSasaRes,
      ] = await Promise.all([
        fetchApp(`${API_CONFIG.baseUrl}/finances/payments`),
        fetchApp(`${API_CONFIG.baseUrl}/finances/invoices`),
        fetchApp(`${API_CONFIG.baseUrl}/finances/mpesa-transactions`),
        fetchApp(`${API_CONFIG.baseUrl}/customers/finance-stats`),
        fetchApp(`${API_CONFIG.baseUrl}/expenses/`),
        fetchApp(`${API_CONFIG.baseUrl}/talksasa/balance`),
      ]);

      setPayments(normalizeList<Payment>(paymentsRes));
      setInvoices(normalizeList<Invoice>(invoicesRes));
      setMpesa(normalizeList<MpesaTx>(mpesaRes));
      setCustomerStats(getCustomerFinanceStats(customerStatsRes));
      setExpenses(normalizeList<Expense>(expensesRes));
      setTalkSasaBalance(talkSasaRes);
    } catch (e) {
      console.error(e);
      setError("Failed to load finance dashboard data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const verifyDashboardPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const password = dashboardPassword.trim();

    if (!password) {
      setPasswordError("Enter your password to unlock the finance dashboard.");
      return;
    }

    try {
      setVerifyingPassword(true);
      setPasswordError(null);

      const res = await fetchApp<{ ok?: boolean; detail?: string }>(
        `${API_CONFIG.baseUrl}/auth/verify-password`,
        {
          method: "POST",
          body: JSON.stringify({ password }),
        },
      );

      if (res && Object.prototype.hasOwnProperty.call(res, "ok") && !res.ok) {
        throw new Error(res.detail || "Invalid password");
      }

      setDashboardPassword("");
      setShowDashboardPassword(false);
      setShowUnlockPage(false);
      setStatsUnlocked(true);
    } catch (e: any) {
      setPasswordError(e?.message || "Invalid password. Please try again.");
    } finally {
      setVerifyingPassword(false);
    }
  };

  const lockDashboard = () => {
    setStatsUnlocked(false);
    setShowUnlockPage(false);
    setDashboardPassword("");
    setPasswordError(null);
    setPayments([]);
    setInvoices([]);
    setMpesa([]);
    setExpenses([]);
    setCustomerStats(null);
    setPackageSearch("");
  };

  useEffect(() => {
    if (!statsUnlocked) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsUnlocked]);

  const kpis = useMemo(() => {
    const now = new Date();
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    let totalRevenueCents = 0;
    let monthRevenueCents = 0;
    let lastMonthRevenueCents = 0;
    let completedPayments = 0;
    let failedPayments = 0;

    payments.forEach((p) => {
      const completed = isCompletedPayment(p.status);

      if (!completed) {
        if (cleanStatus(p.status) === "FAILED") failedPayments += 1;
        return;
      }

      completedPayments += 1;
      totalRevenueCents += p.amount_cents || 0;

      const paidDate = getPaymentDate(p);

      if (!paidDate) return;

      if (paidDate >= startOfThisMonth) {
        monthRevenueCents += p.amount_cents || 0;
      } else if (paidDate >= startOfLastMonth && paidDate < startOfThisMonth) {
        lastMonthRevenueCents += p.amount_cents || 0;
      }
    });

    let totalExpensesCents = 0;
    let monthExpensesCents = 0;
    let lastMonthExpensesCents = 0;

    expenses.forEach((expense) => {
      const cents = expenseToCents(expense);
      totalExpensesCents += cents;

      const date = getExpenseDate(expense);

      if (!date) return;

      if (date >= startOfThisMonth) {
        monthExpensesCents += cents;
      } else if (date >= startOfLastMonth && date < startOfThisMonth) {
        lastMonthExpensesCents += cents;
      }
    });

    const invoicesTotal = invoices.length;

    const invoicesPaid = invoices.filter(
      (i) => cleanStatus(i.status) === "PAID",
    ).length;

    const invoicesPending = invoices.filter(
      (i) => cleanStatus(i.status) === "PENDING",
    ).length;

    const invoicesOverdue = invoices.filter((i) => {
      const dueDate = toDate(i.due_date || null);
      return cleanStatus(i.status) !== "PAID" && dueDate && dueDate < now;
    }).length;

    const totalInvoiceCents = invoices.reduce(
      (sum, invoice) => sum + (invoice.total_amount_cents || 0),
      0,
    );

    const totalPaidInvoiceCents = invoices.reduce(
      (sum, invoice) => sum + (invoice.paid_amount_cents || 0),
      0,
    );

    const receivablesCents = Math.max(
      totalInvoiceCents - totalPaidInvoiceCents,
      0,
    );

    const totalCustomers = customerStats?.total_customers ?? 0;
    const activeCustomers = customerStats?.active_customers ?? 0;
    const inactiveCustomers = customerStats?.inactive_customers ?? 0;
    const newThisMonth = customerStats?.new_this_month ?? 0;
    const expectedMonthlyCents = customerStats?.expected_monthly_cents ?? 0;

    const mpesaSuccessful = mpesa.filter(
      (tx) => tx.processed && !tx.process_error && tx.payment_id,
    ).length;

    const mpesaFailed = mpesa.filter(
      (tx) => tx.process_error || !tx.payment_id,
    ).length;

    const mpesaReceivedCents = mpesa.reduce(
      (sum, tx) => sum + mpesaToCents(tx),
      0,
    );

    const netProfitCents = monthRevenueCents - monthExpensesCents;
    const lastMonthNetProfitCents =
      lastMonthRevenueCents - lastMonthExpensesCents;

    return {
      totalRevenueCents,
      monthRevenueCents,
      lastMonthRevenueCents,
      completedPayments,
      failedPayments,
      avgPaymentCents:
        completedPayments > 0
          ? Math.round(totalRevenueCents / completedPayments)
          : 0,

      invoicesTotal,
      invoicesPaid,
      invoicesPending,
      invoicesOverdue,
      totalInvoiceCents,
      totalPaidInvoiceCents,
      receivablesCents,
      invoiceCollectionRate: percent(totalPaidInvoiceCents, totalInvoiceCents),

      totalCustomers,
      activeCustomers,
      inactiveCustomers,
      newThisMonth,
      expectedMonthlyCents,
      arpuCents:
        activeCustomers > 0
          ? Math.round(monthRevenueCents / activeCustomers)
          : 0,

      totalExpensesCents,
      monthExpensesCents,
      lastMonthExpensesCents,
      netProfitCents,
      lastMonthNetProfitCents,

      mpesaTotal: mpesa.length,
      mpesaSuccessful,
      mpesaFailed,
      mpesaReceivedCents,
      mpesaSuccessRate: percent(mpesaSuccessful, mpesa.length),
    };
  }, [payments, invoices, customerStats, mpesa, expenses]);

  const revenueSeries = useMemo(() => {
    const map = new Map<string, number>();
    const today = new Date();

    for (let i = 29; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      map.set(dayKey(date), 0);
    }

    payments.forEach((payment) => {
      if (!isCompletedPayment(payment.status)) return;

      const date = getPaymentDate(payment);
      if (!date) return;

      const key = dayKey(date);
      if (!map.has(key)) return;

      map.set(key, (map.get(key) || 0) + (payment.amount_cents || 0) / 100);
    });

    return Array.from(map.entries()).map(([date, amount]) => ({
      date,
      label: formatShortDate(date),
      amount,
    }));
  }, [payments]);

  const monthlyCashflow = useMemo(() => {
    const now = new Date();

    const months = Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);

      return {
        key: monthKey(date),
        label: date.toLocaleDateString(undefined, {
          month: "short",
          year: "2-digit",
        }),
        revenue: 0,
        expenses: 0,
        net: 0,
      };
    });

    payments.forEach((payment) => {
      if (!isCompletedPayment(payment.status)) return;

      const date = getPaymentDate(payment);
      if (!date) return;

      const row = months.find((month) => month.key === monthKey(date));
      if (!row) return;

      row.revenue += (payment.amount_cents || 0) / 100;
    });

    expenses.forEach((expense) => {
      const date = getExpenseDate(expense);
      if (!date) return;

      const row = months.find((month) => month.key === monthKey(date));
      if (!row) return;

      row.expenses += expenseToCents(expense) / 100;
    });

    return months.map((row) => ({
      ...row,
      net: row.revenue - row.expenses,
    }));
  }, [payments, expenses]);

  const paymentsByStatus = useMemo(() => {
    const total = payments.length;
    const grouped: Record<string, { value: number; amountCents: number }> = {};

    payments.forEach((payment) => {
      const status = cleanStatus(payment.status);

      if (!grouped[status]) {
        grouped[status] = { value: 0, amountCents: 0 };
      }

      grouped[status].value += 1;
      grouped[status].amountCents += payment.amount_cents || 0;
    });

    return Object.entries(grouped)
      .map(([status, info], index) => ({
        name: status,
        value: info.value,
        amountCents: info.amountCents,
        percent: percent(info.value, total),
        color: STATUS_COLORS[status] || PALETTE[index % PALETTE.length],
      }))
      .filter((entry) => entry.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [payments]);

  const invoicesByStatus = useMemo(() => {
    const grouped: Record<string, { count: number; amount: number }> = {};

    invoices.forEach((invoice) => {
      const status = cleanStatus(invoice.status);

      if (!grouped[status]) {
        grouped[status] = { count: 0, amount: 0 };
      }

      grouped[status].count += 1;
      grouped[status].amount += invoice.total_amount_cents || 0;
    });

    return Object.entries(grouped).map(([status, info], index) => ({
      status,
      count: info.count,
      amount: info.amount / 100,
      fill: STATUS_COLORS[status] || PALETTE[index % PALETTE.length],
    }));
  }, [invoices]);

  const expensesByCategory = useMemo(() => {
    const grouped: Record<string, number> = {};

    expenses.forEach((expense) => {
      const category = expense.category?.trim() || "Uncategorised";
      grouped[category] = (grouped[category] || 0) + expenseToCents(expense);
    });

    return Object.entries(grouped)
      .map(([category, cents], index) => ({
        category,
        amount: cents / 100,
        fill: PALETTE[index % PALETTE.length],
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [expenses]);

  const packagePerformance = useMemo(() => {
    type Perf = {
      planName: string;
      paymentsCount: number;
      monthlyRevenueCents: number;
      totalRevenueCents: number;
    };

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const byPlan: Record<string, Perf> = {};

    payments.forEach((payment) => {
      if (!isCompletedPayment(payment.status)) return;

      const planName = payment.plan_name || "No plan";

      if (!byPlan[planName]) {
        byPlan[planName] = {
          planName,
          paymentsCount: 0,
          monthlyRevenueCents: 0,
          totalRevenueCents: 0,
        };
      }

      byPlan[planName].paymentsCount += 1;
      byPlan[planName].totalRevenueCents += payment.amount_cents || 0;

      const paidDate = getPaymentDate(payment);

      if (paidDate && paidDate >= startOfMonth) {
        byPlan[planName].monthlyRevenueCents += payment.amount_cents || 0;
      }
    });

    let rows = Object.values(byPlan);

    if (packageSearch.trim()) {
      const query = normalizeForSearch(packageSearch);
      rows = rows.filter((row) =>
        normalizeForSearch(row.planName).includes(query),
      );
    }

    return rows.sort((a, b) => b.monthlyRevenueCents - a.monthlyRevenueCents);
  }, [payments, packageSearch]);

  const topCustomers = useMemo<TopCustomerRow[]>(() => {
    const grouped: Record<
      string,
      {
        username: string;
        planName?: string | null;
        totalCents: number;
        paymentsCount: number;
        lastPaidAt?: string | null;
      }
    > = {};

    payments.forEach((payment) => {
      if (!isCompletedPayment(payment.status)) return;

      const key =
        payment.secret_id ||
        payment.secret_username ||
        payment.external_reference ||
        payment.id;

      if (!grouped[key]) {
        grouped[key] = {
          username: payment.secret_username || "Unknown customer",
          planName: payment.plan_name,
          totalCents: 0,
          paymentsCount: 0,
          lastPaidAt: null,
        };
      }

      grouped[key].totalCents += payment.amount_cents || 0;
      grouped[key].paymentsCount += 1;

      const paidAt = payment.paid_at || payment.created_at;

      if (
        paidAt &&
        (!grouped[key].lastPaidAt ||
          new Date(paidAt) > new Date(grouped[key].lastPaidAt!))
      ) {
        grouped[key].lastPaidAt = paidAt;
      }
    });

    return Object.entries(grouped)
      .map(([key, info]) => ({
        key,
        username: info.username,
        plan_name: info.planName,
        totalReceivedCents: info.totalCents,
        paymentsCount: info.paymentsCount,
        lastPaidAt: info.lastPaidAt,
      }))
      .sort((a, b) => b.totalReceivedCents - a.totalReceivedCents)
      .slice(0, 10);
  }, [payments]);

  const mpesaHealthRows = useMemo(() => {
    return [...mpesa]
      .sort((a, b) => {
        const da = getMpesaDate(a)?.getTime() || 0;
        const db = getMpesaDate(b)?.getTime() || 0;
        return db - da;
      })
      .slice(0, 5);
  }, [mpesa]);

  const monthDiffCents = kpis.monthRevenueCents - kpis.lastMonthRevenueCents;

  const monthDiffPercent =
    kpis.lastMonthRevenueCents > 0
      ? ((monthDiffCents / kpis.lastMonthRevenueCents) * 100).toFixed(1)
      : "0.0";

  const expenseDiffCents =
    kpis.monthExpensesCents - kpis.lastMonthExpensesCents;

  const expenseDiffPercent =
    kpis.lastMonthExpensesCents > 0
      ? ((expenseDiffCents / kpis.lastMonthExpensesCents) * 100).toFixed(1)
      : "0.0";

  const netDiffCents = kpis.netProfitCents - kpis.lastMonthNetProfitCents;

  const netDiffPercent =
    Math.abs(kpis.lastMonthNetProfitCents) > 0
      ? ((netDiffCents / Math.abs(kpis.lastMonthNetProfitCents)) * 100).toFixed(
          1,
        )
      : "0.0";

  if (!statsUnlocked) {
    const lockedCards = [
      { label: "Total Revenue", icon: <CreditCard className="h-5 w-5" /> },
      { label: "This Month", icon: <TrendingUp className="h-5 w-5" /> },
      { label: "Net Profit", icon: <Banknote className="h-5 w-5" /> },
      { label: "Receivables", icon: <FileText className="h-5 w-5" /> },
      { label: "Active Customers", icon: <Users className="h-5 w-5" /> },
      { label: "Expected Monthly", icon: <Wallet className="h-5 w-5" /> },
      { label: "ARPU", icon: <Activity className="h-5 w-5" /> },
      { label: "M-Pesa Success", icon: <Receipt className="h-5 w-5" /> },
    ];

    return (
      <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px] space-y-6">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            <div className="relative overflow-hidden">
              <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
              <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

              <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Finance Control Center
                  </div>

                  <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                    Finance Overview
                  </h1>

                  <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                    Dashboard stats are hidden. Tap the eye icon and enter your
                    account password to view revenue, expenses, invoices,
                    customers, and M-Pesa statistics.
                  </p>

                  <p className="mt-2 text-[12px] font-mono font-bold text-slate-400">
                    {new Date().toLocaleDateString("en-KE", {
                      year: "numeric",
                      month: "long",
                    })}{" "}
                    · Protected stats
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setPasswordError(null);
                    setDashboardPassword("");
                    setShowDashboardPassword(false);
                    setShowUnlockPage(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 text-[13px] font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98]"
                >
                  <Eye className="h-4 w-4" />
                  Show Stats
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-5 shadow-xl shadow-slate-300/25 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-600 shadow-sm">
                  <LockKeyhole className="h-6 w-6" />
                </div>

                <div>
                  <p className="text-[12px] font-black uppercase tracking-[0.12em] text-emerald-700">
                    Stats Locked
                  </p>
                  <h2 className="mt-1 text-xl font-black text-slate-900">
                    Sensitive finance numbers are protected
                  </h2>
                  <p className="mt-1 max-w-2xl text-[13px] font-medium leading-6 text-slate-500">
                    The dashboard will only fetch and reveal the actual numbers
                    after your password is verified.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setPasswordError(null);
                  setDashboardPassword("");
                  setShowDashboardPassword(false);
                  setShowUnlockPage(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-black text-slate-700 shadow-sm transition-all hover:bg-slate-100 active:scale-[0.98]"
              >
                <Eye className="h-4 w-4 text-emerald-600" />
                View Dashboard Stats
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {lockedCards.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setPasswordError(null);
                    setDashboardPassword("");
                    setShowDashboardPassword(false);
                    setShowUnlockPage(true);
                  }}
                  className="group rounded-3xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-100/70"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 transition group-hover:bg-emerald-50 group-hover:text-emerald-600">
                      {item.icon}
                    </div>

                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <Eye className="h-4 w-4" />
                    </div>
                  </div>

                  <p className="mt-4 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    {item.label}
                  </p>

                  <div className="mt-2 h-7 w-32 rounded-full bg-slate-200/80 blur-[1px]" />
                  <div className="mt-2 h-3 w-44 rounded-full bg-slate-100" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {showUnlockPage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
            <div className="w-full max-w-md overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-950/30">
              <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

              <div className="relative overflow-hidden p-6 sm:p-7">
                <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-emerald-200/40 blur-2xl" />
                <div className="absolute -bottom-20 -left-12 h-44 w-44 rounded-full bg-cyan-200/40 blur-2xl" />

                <div className="relative">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 text-emerald-600 shadow-lg shadow-emerald-200/40">
                    <ShieldCheck className="h-8 w-8" />
                  </div>

                  <div className="mt-5 text-center">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-emerald-700">
                      Password Required
                    </p>

                    <h1 className="mt-2 text-2xl font-black tracking-[-0.5px] text-slate-900">
                      Unlock Dashboard Stats
                    </h1>

                    <p className="mt-2 text-[13px] font-medium leading-6 text-slate-500">
                      Enter your account password to reveal the finance
                      dashboard statistics.
                    </p>
                  </div>

                  <form
                    onSubmit={verifyDashboardPassword}
                    className="mt-6 space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="finance-dashboard-password"
                        className="text-[12px] font-black text-slate-700"
                      >
                        Password
                      </label>

                      <div className="relative mt-1.5">
                        <LockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                        <input
                          id="finance-dashboard-password"
                          type={showDashboardPassword ? "text" : "password"}
                          value={dashboardPassword}
                          onChange={(e) => {
                            setDashboardPassword(e.target.value);
                            if (passwordError) setPasswordError(null);
                          }}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          autoFocus
                          className="h-12 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-11 text-[13px] font-bold text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setShowDashboardPassword((prev) => !prev)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                          aria-label={
                            showDashboardPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          {showDashboardPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {passwordError && (
                      <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] font-bold text-rose-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>{passwordError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={verifyingPassword}
                      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 text-[13px] font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {verifyingPassword ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ShieldCheck className="h-4 w-4" />
                      )}
                      {verifyingPassword ? "Verifying…" : "Unlock Stats"}
                    </button>

                    <button
                      type="button"
                      disabled={verifyingPassword}
                      onClick={() => {
                        setShowUnlockPage(false);
                        setDashboardPassword("");
                        setPasswordError(null);
                        setShowDashboardPassword(false);
                      }}
                      className="inline-flex h-11 w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-[13px] font-black text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </form>

                  <p className="mt-4 text-center text-[11px] font-medium leading-5 text-slate-400">
                    Dashboard data is not loaded until this password is
                    verified.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (
    loading &&
    !payments.length &&
    !invoices.length &&
    !expenses.length &&
    !customerStats
  ) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#eef3f8]">
        <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
        </div>

        <p className="text-[13px] font-bold text-slate-500">
          Loading finance dashboard…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-start gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Finance Control Center
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  Finance Overview
                </h1>

                <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                  Track revenue, expenses, M-Pesa callbacks, invoices, packages,
                  and customer payment performance.
                </p>

                <p className="mt-2 text-[12px] font-mono font-bold text-slate-400">
                  {new Date().toLocaleDateString("en-KE", {
                    year: "numeric",
                    month: "long",
                  })}{" "}
                  · All amounts in KES
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={lockDashboard}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-black text-slate-700 shadow-sm transition-all hover:bg-slate-100 active:scale-[0.98]"
                >
                  <LockKeyhole className="h-4 w-4" />
                  Lock Stats
                </button>

                <button
                  type="button"
                  onClick={loadDashboard}
                  disabled={refreshing}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 text-[13px] font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Total Revenue"
            value={formatKES(kpis.totalRevenueCents)}
            sub={`${kpis.completedPayments} completed payments`}
            icon={<CreditCard className="h-5 w-5" />}
            tone="emerald"
          />

          <KpiCard
            label="This Month"
            value={formatKES(kpis.monthRevenueCents)}
            sub={`Last month: ${formatKES(kpis.lastMonthRevenueCents)}`}
            icon={<TrendingUp className="h-5 w-5" />}
            tone="sky"
          />

          <KpiCard
            label="Net Profit"
            value={formatKES(kpis.netProfitCents)}
            sub="Revenue less expenses"
            icon={<Banknote className="h-5 w-5" />}
            tone={kpis.netProfitCents >= 0 ? "emerald" : "rose"}
          />

          <KpiCard
            label="Receivables"
            value={formatKES(kpis.receivablesCents)}
            sub={`${kpis.invoiceCollectionRate}% invoice collection`}
            icon={<FileText className="h-5 w-5" />}
            tone="amber"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Active Customers"
            value={`${kpis.activeCustomers} / ${kpis.totalCustomers}`}
            sub={`${kpis.inactiveCustomers} inactive · ${kpis.newThisMonth} new`}
            icon={<Users className="h-5 w-5" />}
            tone="violet"
          />

          <KpiCard
            label="Expected Monthly"
            value={formatKES(kpis.expectedMonthlyCents)}
            sub="Based on payable amounts"
            icon={<Wallet className="h-5 w-5" />}
            tone="cyan"
          />

          <KpiCard
            label="ARPU"
            value={formatKES(kpis.arpuCents)}
            sub="Revenue per active user"
            icon={<Activity className="h-5 w-5" />}
            tone="sky"
          />

          <KpiCard
            label="M-Pesa Success"
            value={`${kpis.mpesaSuccessRate}%`}
            sub={`${kpis.mpesaSuccessful} successful · ${kpis.mpesaFailed} failed`}
            icon={<Receipt className="h-5 w-5" />}
            tone="emerald"
          />
          <KpiCard
            label="SMS Balance"
            value={
              talkSasaBalance?.balance != null
                ? `${talkSasaBalance.currency || "Ksh"} ${talkSasaBalance.balance}`
                : "—"
            }
            sub="TalkSasa SMS credits"
            icon={<Activity className="h-5 w-5" />}
            tone="cyan"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <MovementCard
            label="Revenue Movement"
            value={formatKES(kpis.monthRevenueCents)}
            last={`Last month: ${formatKES(kpis.lastMonthRevenueCents)}`}
            diffCents={monthDiffCents}
            diffPercent={monthDiffPercent}
          />

          <MovementCard
            label="Expense Movement"
            value={formatKES(kpis.monthExpensesCents)}
            last={`Last month: ${formatKES(kpis.lastMonthExpensesCents)}`}
            diffCents={expenseDiffCents}
            diffPercent={expenseDiffPercent}
            tone={expenseDiffCents <= 0 ? "good" : "bad"}
          />

          <MovementCard
            label="Net Movement"
            value={formatKES(kpis.netProfitCents)}
            last={`Last month: ${formatKES(kpis.lastMonthNetProfitCents)}`}
            diffCents={netDiffCents}
            diffPercent={netDiffPercent}
            tone={netDiffCents >= 0 ? "good" : "bad"}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <PanelWrap
            title="Daily Revenue · Last 30 Days"
            hint="Completed payments"
          >
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueSeries}>
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_STYLE}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={20}
                  />
                  <YAxis
                    tick={AXIS_STYLE}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v / 1000}k`}
                  />
                  <Tooltip
                    content={<KesTooltip />}
                    cursor={{ stroke: "#94a3b8", strokeWidth: 1 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    name="Revenue"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 5, fill: "#10b981", strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </PanelWrap>

          <PanelWrap title="Monthly Cashflow" hint="Revenue, expenses and net">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyCashflow} barSize={18}>
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_STYLE}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={AXIS_STYLE}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v / 1000}k`}
                  />
                  <Tooltip
                    content={<KesTooltip />}
                    cursor={{ fill: "#e2e8f0" }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => (
                      <span style={{ fontSize: 11, color: "#475569" }}>
                        {value}
                      </span>
                    )}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="#10b981"
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="expenses"
                    name="Expenses"
                    fill="#ef4444"
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="net"
                    name="Net"
                    fill="#06b6d4"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </PanelWrap>

          <PanelWrap
            title="Payments by Status"
            hint={`${payments.length} total payment records`}
            right={
              <span className="rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[10px] font-black text-slate-600">
                DONUT
              </span>
            }
          >
            {paymentsByStatus.length === 0 ? (
              <EmptyState
                message="No payment status data"
                detail="Payment status distribution will appear after payments are recorded."
              />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Tooltip content={<PaymentStatusTooltip />} />
                    <Pie
                      data={paymentsByStatus}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="47%"
                      innerRadius={54}
                      outerRadius={86}
                      paddingAngle={3}
                      minAngle={4}
                      labelLine={false}
                      label={({ name, percent: piePercent = 0 }) =>
                        `${name} ${(piePercent * 100).toFixed(0)}%`
                      }
                    >
                      {paymentsByStatus.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span style={{ fontSize: 11, color: "#475569" }}>
                          {value}
                        </span>
                      )}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            )}
          </PanelWrap>

          <PanelWrap
            title="Invoices by Status"
            hint={`${kpis.invoicesTotal} total invoices`}
          >
            {invoicesByStatus.length === 0 ? (
              <EmptyState
                message="No invoices yet"
                detail="Invoice status analytics will appear once invoices are generated."
              />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={invoicesByStatus} barSize={34}>
                    <CartesianGrid {...CHART_GRID} />
                    <XAxis
                      dataKey="status"
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CountTooltip />} />
                    <Bar dataKey="count" name="Invoices" radius={[8, 8, 0, 0]}>
                      {invoicesByStatus.map((entry) => (
                        <Cell key={entry.status} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </PanelWrap>

          <PanelWrap title="Expenses by Category" hint="Top expense groups">
            {expensesByCategory.length === 0 ? (
              <EmptyState
                message="No expenses recorded"
                detail="Expense categories will appear once expenses are added."
              />
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={expensesByCategory} barSize={24}>
                    <CartesianGrid {...CHART_GRID} />
                    <XAxis
                      dataKey="category"
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={AXIS_STYLE}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v / 1000}k`}
                    />
                    <Tooltip
                      content={<KesTooltip />}
                      cursor={{ fill: "#e2e8f0" }}
                    />
                    <Bar dataKey="amount" name="Expenses" radius={[8, 8, 0, 0]}>
                      {expensesByCategory.map((entry) => (
                        <Cell key={entry.category} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </PanelWrap>

          <PanelWrap
            title="Customer Summary"
            hint="From customer finance stats"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-3xl border border-slate-200 bg-slate-100 p-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Total Customers
                </p>
                <p className="mt-1 text-2xl font-black text-slate-900">
                  {kpis.totalCustomers}
                </p>
              </div>

              <div className="rounded-3xl border border-emerald-200 bg-emerald-100 p-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-emerald-700">
                  Active
                </p>
                <p className="mt-1 text-2xl font-black text-emerald-800">
                  {kpis.activeCustomers}
                </p>
              </div>

              <div className="rounded-3xl border border-rose-200 bg-rose-100 p-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-rose-600">
                  Inactive
                </p>
                <p className="mt-1 text-2xl font-black text-rose-700">
                  {kpis.inactiveCustomers}
                </p>
              </div>

              <div className="rounded-3xl border border-indigo-200 bg-indigo-100 p-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-indigo-600">
                  New This Month
                </p>
                <p className="mt-1 text-2xl font-black text-indigo-700">
                  {kpis.newThisMonth}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-cyan-200 bg-cyan-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest text-cyan-700">
                    Expected Monthly Revenue
                  </p>

                  <p className="mt-1 text-xl font-black text-slate-900">
                    {formatKES(kpis.expectedMonthlyCents)}
                  </p>
                </div>

                <Wallet className="h-8 w-8 text-cyan-600" />
              </div>
            </div>
          </PanelWrap>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <PanelWrap
            title="Package Performance"
            hint="Completed payments grouped by plan"
            right={
              <div className="relative w-52">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />

                <input
                  value={packageSearch}
                  onChange={(e) => setPackageSearch(e.target.value)}
                  placeholder="Search package"
                  className="h-9 w-full rounded-2xl border border-slate-300 bg-slate-50 pl-9 pr-3 text-[12px] font-medium text-slate-700 outline-none transition-all focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                />
              </div>
            }
          >
            {packagePerformance.length === 0 ? (
              <EmptyState
                message="No package data yet"
                detail="Package revenue will appear when completed payments include plan names."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-3">Package</th>
                      <th className="px-4 py-3">Payments</th>
                      <th className="px-4 py-3">This Month</th>
                      <th className="px-4 py-3 text-right">Total</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                    {packagePerformance.map((plan) => (
                      <tr
                        key={plan.planName}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-4 py-3 font-black text-slate-800">
                          {plan.planName}
                        </td>

                        <td className="px-4 py-3">
                          <span className="rounded-full border border-indigo-200 bg-indigo-100 px-2.5 py-1 text-[11px] font-black text-indigo-700">
                            {plan.paymentsCount}
                          </span>
                        </td>

                        <td className="px-4 py-3 font-mono font-black text-slate-800">
                          {formatKES(plan.monthlyRevenueCents)}
                        </td>

                        <td className="px-4 py-3 text-right font-mono font-black text-emerald-700">
                          {formatKES(plan.totalRevenueCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PanelWrap>

          <PanelWrap
            title="Latest M-Pesa Transactions"
            hint={`${kpis.mpesaTotal} received callbacks`}
            right={<PieChartIcon className="h-4 w-4 text-slate-400" />}
          >
            {mpesaHealthRows.length === 0 ? (
              <EmptyState
                message="No M-Pesa data"
                detail="Callback records will appear here once payments are received."
              />
            ) : (
              <div className="space-y-3">
                {mpesaHealthRows.map((tx) => {
                  const success =
                    tx.processed && !tx.process_error && tx.payment_id;

                  return (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-slate-100/70 p-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[12.5px] font-black text-slate-800">
                          {tx.first_name ||
                            tx.raw_request?.FirstName ||
                            tx.msisdn ||
                            tx.raw_request?.MSISDN ||
                            "Unknown payer"}
                        </p>

                        <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                          {tx.trans_id ||
                            tx.raw_request?.TransID ||
                            tx.bill_ref_number ||
                            tx.raw_request?.BillRefNumber ||
                            "No reference"}
                        </p>

                        <p className="mt-0.5 text-[10.5px] font-bold text-slate-400">
                          {formatDate(tx.received_at || tx.processed_at)}
                        </p>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-[12.5px] font-black text-slate-800">
                          {formatKES(mpesaToCents(tx))}
                        </p>

                        <span
                          className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black ${
                            success
                              ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                              : "border-rose-200 bg-rose-100 text-rose-700"
                          }`}
                        >
                          {success ? "Processed" : "Issue"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PanelWrap>
        </div>

        <PanelWrap
          title="Top 10 Customers by Revenue"
          hint="Based on completed payments only"
        >
          {topCustomers.length === 0 ? (
            <EmptyState
              message="No payment data yet"
              detail="Once customers start paying, the top customers list will appear here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Package</th>
                    <th className="px-4 py-3">Payments</th>
                    <th className="px-4 py-3">Last Paid</th>
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                  {topCustomers.map((customer) => (
                    <tr
                      key={customer.key}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-4 py-3">
                        <p className="font-black text-slate-800">
                          {customer.username}
                        </p>
                      </td>

                      <td className="px-4 py-3 font-medium text-slate-500">
                        {customer.plan_name || "No plan"}
                      </td>

                      <td className="px-4 py-3">
                        <span className="rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                          {customer.paymentsCount}
                        </span>
                      </td>

                      <td className="px-4 py-3 font-medium text-slate-500">
                        {formatDate(customer.lastPaidAt)}
                      </td>

                      <td className="px-4 py-3 text-right font-mono font-black text-emerald-700">
                        {formatKES(customer.totalReceivedCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PanelWrap>
      </div>
    </div>
  );
};

export default FinanceDashboardPage;
